
import { fetchAvailabilityCalendarForMinimumQuantities } from '@/api/Event'
import type { EventDetails, LinkedTG } from '@/api/Helpers'
import { fetchSessionsForDateForMinimumQuantities, Session } from '@/api/Sessions'
import NavigateBack from '@/components/elements/NavigateBack.vue'
import RenewMembershipMessage from '@/components/elements/RenewMembershipMessage.vue'
import SlideInOut from '@/components/elements/SlideInOut.vue'
import AdmitDetailsFields from '@/components/events/AdmitDetailsFields.vue'
import CollapsibleWithSummary from '@/components/events/CollapsibleWithSummary.vue'
import SelectAvailableSession from '@/components/events/SelectAvailableSession.vue'
import SelectDate from '@/components/events/SelectDate.vue'
import SelectQuantities from '@/components/events/SelectQuantities.vue'
import FormInput2 from '@/components/forms/FormInput2.vue'
import Survey2 from '@/components/forms/Survey2.vue'
import environment, { portal } from '@/helpers/Environment'
import { languageItem, reserveLanguage } from '@/helpers/LanguageHelpers'
import { getHideSoldOutSessionsConfig } from '@/helpers/SessionHelpers'
import { surveySpecsWithTicketGroupId, SurveySpecWithTicketGroupID } from '@/helpers/SurveyHelpers'
import { sumQuantities } from '@/helpers/TicketTypeQuantities'
import { reportFormValidity } from '@/helpers/Validation'
import { reserveSubmitHandler, ReserveSubmitOptions } from '@/state/Reserve'
import moment from 'moment-timezone'
import { Moment } from 'moment-timezone/moment-timezone'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import MobileFooterPortal from '../cart/MobileFooterPortal.vue'
import ReserveMobileFooter from '../cart/ReserveMobileFooter.vue'
import { AnnotatedSession, annotateSession, uniqueAnnotations } from '@/helpers/DynamicMessages'
import DynamicMessageGroup from '@/components/events/DynamicMessageGroup.vue'
import MobileCartButton from '@/components/elements/MobileCartButton.vue'
import MobileTotal from '@/components/elements/MobileTotal.vue'
import { isChainedUpsell } from '@/helpers/EventHelpers'
import EventDetailErrorMessages from '@/components/events/EventDetailErrorMessages.vue'
import { reserveTicketsPayload } from '@/helpers/Reserve'

/**
 * TODO Add support for admission passes & split this into smaller components.
 * @see https://trello.com/c/fYgJ68Px/
 */
@Component({
  name: 'ReserveQuantityFirst',
  components: {
    MobileTotal,
    MobileCartButton,
    SlideInOut,
    CollapsibleWithSummary,
    AdmitDetailsFields,
    FormInput2,
    NavigateBack,
    SelectDate,
    SelectQuantities,
    SelectAvailableSession,
    Survey2,
    MobileFooterPortal,
    ReserveMobileFooter,
    RenewMembershipMessage,
    DynamicMessageGroup,
    EventDetailErrorMessages,
  },
})
export default class extends Vue {
  @Prop({ required: true })
  event: EventDetails

  @Prop()
  contexts: Set<string>

  readonly formId = 'reserve-quantity-first-form'

  quantitiesClosed: boolean = false
  selectedTicketTypes: TicketTypeQuantities = {}

  dateClosed: boolean = false
  dates: Dictionary | null = null
  selectedDate: Moment | null = null

  sessionClosed: boolean = false
  allSessions: Session[] | null = null
  selectedSession: Session | null = null

  surveyAnswers: Dict<Dict<Primitive>> = {}
  details: Dict<EventAdmitDetails[]> = {}

  submitting: boolean = false
  apiError: any = null

  l = reserveLanguage('reserveQuantityFirst')

  get quantitiesTotal() {
    return sumQuantities(this.selectedTicketTypes)
  }

  get totalAvailableDates(): number {
    const available = Object.values(this.dates!).filter((status) => status === 'available')
    return available.length
  }

  get availableSessions(): AnnotatedSession[] {
    const sessions = this.allSessions ? this.allSessions.filter((session) => !session.sold_out) : []
    return sessions.map((session) => annotateSession(session, this.event, this.selectedTicketTypes))
  }

  // TODO Deduplicate from <ReserveDateFirst>.
  get soldOutSessions(): Session[] {
    return this.allSessions ? this.allSessions.filter((session) => session.sold_out) : []
  }

  get showSoldOutTimes(): boolean {
    const hide = getHideSoldOutSessionsConfig(this.event, environment)
    return !hide && this.soldOutSessions.length > 0
  }

  // TODO Deduplicate from <ReserveDateFirst>.
  joinedSessionTimes(sessions: Session[]) {
    return sessions.map((session) => session.startMoment.format('h:mm A')).join(', ')
  }

  get selectSessionLabel() {
    return languageItem('selectAvailableSession').title
  }

  get selectedGroups(): LinkedTG[] {
    return this.event.ticketGroups.filter((group) => {
      return group.types.some((type) => this.selectedTicketTypes[type.id]?.quantity > 0)
    })
  }

  get surveySpecs(): SurveySpecWithTicketGroupID[] {
    return surveySpecsWithTicketGroupId(this.selectedGroups)
  }

  @Watch('selectedSession')
  onSessionChange(selectedSession: Session) {
    selectedSession ? this.closeSessions() : this.openSessions()
  }

  @Watch('selectedDate')
  onDateChange(selectedDate: Date) {
    selectedDate ? this.closeDate() : this.openDate()
  }

  @Watch('event')
  openQuantities() {
    this.quantitiesClosed = false
    this.resetSessions()
    this.resetDates()
  }

  closeQuantities() {
    this.quantitiesClosed = true

    fetchAvailabilityCalendarForMinimumQuantities(this.event.id, this.selectedTicketTypes).then((response) => {
      this.dates = response
    })
  }

  openDate() {
    this.dateClosed = false
    this.resetSessions()
  }

  closeDate() {
    this.dateClosed = true

    fetchSessionsForDateForMinimumQuantities(
      this.event.id,
      this.event.venue,
      this.selectedDate!,
      this.selectedTicketTypes,
    ).then(({ sessions }) => {
      this.allSessions = sessions
    })
  }

  openSessions() {
    this.sessionClosed = false
  }

  closeSessions() {
    this.sessionClosed = true
  }

  resetSessions() {
    this.allSessions = null
    this.selectedSession = null
    this.sessionClosed = false
    this.apiError = null
  }

  resetDates() {
    this.dates = null
    this.selectedDate = null
    this.dateClosed = false
    this.apiError = null
  }

  onSubmit() {
    this.submit()
  }

  submit(codes?: string[]): Promise<void> {
    const submitOptions: ReserveSubmitOptions = {
      event: this.event,
      ticketsPayload: reserveTicketsPayload(this.selectedTicketTypes, this.selectedSession, this.details, undefined),
      surveyAnswers: this.surveyAnswers,
      selectedGroups: this.selectedGroups,
      emitCallback: this.$emit.bind(this),
      codes: codes,
    }

    return this.validate()
      .then(() => {
        this.submitting = true
      })
      .then(() => reserveSubmitHandler(submitOptions))
      .catch((error) => {
        this.apiError = error
      })
      .finally(() => {
        this.submitting = false
      })
  }

  validate() {
    return reportFormValidity(this.$refs.form as HTMLFormElement)
  }

  get earliestDate(): Moment {
    return this.selectedDate || moment.tz(this.dates?.[0], portal.timezone)
  }

  get sessionAnnotations(): MessageAction[] {
    return uniqueAnnotations(this.availableSessions)
  }

  get showCart() {
    return this.$store.getters['Cart/ticketCount'] > 0
  }

  get isChainedUpsell() {
    return isChainedUpsell(this.$route)
  }
}
